// deployment_class: customer_env_allowed
/**
 * Import Data — the container for bringing data in, without leaving the module.
 *
 * ── The template is AGNOSTIC (E3 2026-08-29) ───────────────────────────────
 * E3, correcting an earlier version of this file: *"We're creating a template,
 * and a template doesn't have anything to do with previous data… Our template
 * is agnostic to any project and any data. It is simply a container to display
 * data. There is no preconceived, preconfigured pathway to any previous or
 * known data within the database."*
 *
 * The earlier version fetched `/v3/projects` and offered Willow, Harbor Point
 * and Willow Creek as choices. That was wrong twice over: it wired the template
 * to specific rows, and the rows it surfaced were demo remnants.
 *
 * ── And why a picker is nonetheless correct (E3 2026-09-08) ────────────────
 * E3: *"we must create the uploader process so that our data superconductor
 * works as expected and as designed/spec'd."*
 *
 * These two do not conflict, and the distinction is the whole design:
 *
 *   The TEMPLATE knowing about rows          → wrong. It ships pointing at
 *                                              nothing and still does.
 *   A USER naming which of THEIR projects    → right. That is attribution
 *   this document belongs to, at use time      GIVEN, which is the rule the
 *                                              import path is built on.
 *
 * Nothing is fetched until a customer opens this tab and asks; what comes back
 * is their own organisation's projects, from the live API, never a literal in
 * this file. The selection lives in component state and is gone on unmount —
 * the template records no project, before or after.
 *
 * ── Why not infer the project instead of asking ────────────────────────────
 * `POST /v3/projects/:project_id/imports` takes the project in its PATH
 * deliberately: *"Attribution is GIVEN, never inferred. The Superconductor was
 * built to guess which project a document belonged to, carrying a confidence
 * score and a quarantine branch for when it could not tell. Taking the project
 * from the context the user is standing in removes that guess."* Asking IS the
 * mechanism. A picker is not a concession to it.
 *
 * ── The rule it keeps ──────────────────────────────────────────────────────
 * E3: *"I don't want another menu item or an import data superconductor page
 * that's just gonna confuse the user and take them away from the workflow…
 * If we can do it all with almost one page, all the better."*
 *
 * Importing is an ACTION INSIDE the module, never a destination. **Nothing here
 * navigates** — which is also why an agency with no projects yet can create one
 * from this tab rather than being sent to `/projects/new` and losing the file
 * they were about to drop.
 *
 * ── And the drop location decides nothing ──────────────────────────────────
 * Dropping a file here says "I am here, take this" — not "this is project
 * data". The classifier reads the document and decides where it belongs. Being
 * in this module when you add something must not force a wrong home onto it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ImportPanel } from '../projects/create/ImportPanel';
import { apiFetch } from '../shell/api/api-client-base';
import { createProject, type ProjectTrack } from '../projects/create/api';

/** One of the customer's own projects, as the API returns it. */
interface ProjectRow {
  id: string;
  name: string;
  track: ProjectTrack;
}

const TRACK_LABEL: Record<ProjectTrack, string> = {
  workflow: 'Workflow',
  development: 'Development',
  acquisition: 'Acquisition',
};

export function ImportDataTab({
  /**
   * The project in context, when there is one — e.g. the customer already has a
   * project open. A template has none, so the unbound state is the normal one
   * here rather than an error, and the picker below is how it gets bound.
   */
  projectId = null,
  /**
   * True while a developer is AUTHORING THE SHAPE rather than a customer using
   * the dashboard. A template holds no data, so this tab shows the picker's
   * shape and asks the server nothing.
   *
   * ── Why this prop had to exist (E3 2026-09-09, with a screenshot) ─────────
   * *"the template cannot have Dev_1a in the template. That is a bug."*
   *
   * The header of this file already stated the resolution: the TEMPLATE must
   * not point at known rows, the UPLOADER must work, and the two "resolve on
   * WHO names the project". That resolution was agreed and never WIRED —
   * nothing told this component which of the two it was inside, so a developer
   * authoring the shape and a customer opening it unbound looked identical from
   * in here, and both got a live `/v3/projects` fetch.
   *
   * ── Why `editingTemplate` and not `bare` ─────────────────────────────────
   * `bare` in Dashboard.tsx answers TWO questions at once — "did a supplier show
   * up?" and "am I editing the shape?". Only the second one applies here: this
   * tab's data never came from the content supplier, it comes from the API. A
   * tab gated on `bare` would go dead for a real customer whenever no supplier
   * was bound, breaking import. Reusing a flag because it is nearby, for a
   * question it does not ask, is how `bare` gated `TabSummary` while four tabs
   * kept rendering (PR #369).
   *
   * ── Why the FETCH is suppressed and not just the rows ────────────────────
   * Hiding rows would leave the request firing: a template quietly reading
   * customer data and declining to paint it. The blank slate is about what the
   * template TOUCHES, not only what it shows
   * ([[feedback_wire_it_or_dont_build_it_never_satisfy_the_checker_2026_08_20]]).
   */
  editingTemplate = false,
}: {
  projectId?: string | null;
  editingTemplate?: boolean;
}): JSX.Element {
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** What the CUSTOMER chose. Never persisted — the template records no project. */
  const [chosen, setChosen] = useState<string | null>(null);

  // Creating one without leaving the tab.
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    apiFetch<ProjectRow[]>('/v3/projects', signal ? { signal } : {})
      .then((r) => {
        // A non-array body is a contract break, not an empty agency. Treating
        // it as "no projects" would show the create form over a broken API and
        // invite a duplicate project.
        if (!Array.isArray(r)) throw new Error('Unexpected response from the projects service.');
        setRows(r);
        setError(null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (signal?.aborted === true) return;
        setError(err instanceof Error ? err.message : 'Please try again.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    // A template asks the server nothing. This is the load-bearing line of the
    // blank-slate fix: the guard is on the REQUEST, so no customer row is ever
    // read into a surface that holds no data.
    if (editingTemplate) {
      // ── And DISCARD anything already fetched ────────────────────────────
      // Suppressing the request is not enough on the path a person actually
      // takes. The component does not unmount when edit mode is entered, so a
      // customer who opened this tab first (rows loaded) and THEN clicked
      // "Edit template" kept every row in state — and `rows.length > 0` still
      // rendered the "New project" button over the blank slate.
      //
      // Found by SCREENSHOT, not by test: the unit and integration tests both
      // mounted fresh already in edit mode, so neither ever crossed the
      // transition. The rendered app is the only thing that knows what the
      // rendered app does ([[feedback_always_visually_verify_ui_via_cdp_never_playwright_2026_07_19]]).
      setRows([]);
      setChosen(null);
      setCreating(false);
      setError(null);
      setLoading(false);
      return;
    }
    // Only when unbound: with a project already in context there is nothing to
    // choose, so there is no reason to ask the server for a list.
    if (projectId !== null) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [projectId, load, editingTemplate]);

  /**
   * The project this tab is actually importing into, if any.
   *
   * Forced unbound while editing: `projectId` is a real binding from the
   * customer's context, and letting it through would render the uploader
   * against a live project from inside the template.
   */
  const active = editingTemplate ? null : (projectId ?? chosen);

  async function submitNew(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const name = newName.trim();
    if (name === '' || saving) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createProject({ name });
      // Select it immediately — the customer came here to import, and making
      // them find the thing they just made is the friction this tab exists to
      // avoid.
      setRows((r) => [...r, { id: created.id, name: created.name, track: created.track }]);
      setChosen(created.id);
      setCreating(false);
      setNewName('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The project could not be created.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="kd-import">
      <div className="kd-import-h">
        <h2>Import data</h2>
        <p>
          Anything the work runs on — a rent roll, a workbook, a pro-forma, a signed agreement.
          Each one is read on arrival and filed where it belongs; where it is dropped is a
          convenience, not an instruction.
        </p>
      </div>

      {error !== null && (
        <div className="kd-import-error" role="alert">
          {error}
        </div>
      )}

      {/* The picker appears only while nothing is bound. Once a project is in
          context the tab is an uploader and nothing else. */}
      {active === null && (
        <div className="kd-import-pick">
          <div className="kd-import-pick-h">
            <span>Which project is this for?</span>
            {/* `!editingTemplate` is asserted here as well as in the effect
                above. Belt AND braces on purpose: this button was gated on
                `rows.length > 0` alone, so it survived the view→edit transition
                on stale rows and rendered over the blank slate. A control whose
                visibility depends only on data the surface should not have is
                one state-timing bug away from appearing. */}
            {!editingTemplate && !creating && rows.length > 0 && (
              <button type="button" onClick={() => setCreating(true)}>
                New project
              </button>
            )}
          </div>

          {editingTemplate ? (
            /* The SHAPE of the picker and nothing else. The question above is
               part of the element being arranged, so an editor can see and place
               it; the answers are the customer's data and are not here.

               This branch is FIRST and explicit rather than falling through to
               the empty-state form below, because with no rows that form would
               render "There are no projects yet" — a claim ABOUT CUSTOMER DATA
               made by a surface that deliberately never looked. A blank slate
               that asserts an agency has no projects is not blank, it is wrong. */
            <p className="kd-import-note">
              A project is named here when someone imports. The template records none.
            </p>
          ) : loading ? (
            <p className="kd-import-note">Loading your projects…</p>
          ) : creating || rows.length === 0 ? (
            <form className="kd-import-new" onSubmit={submitNew}>
              {rows.length === 0 && !creating && (
                <p className="kd-import-note" style={{ gridColumn: '1 / -1' }}>
                  There are no projects yet. Name the first one and the upload continues here —
                  you are not taken anywhere.
                </p>
              )}
              {/* Name only. The Track control that stood here asked "what kind
                  of project is this?" on a form whose heading asks "which
                  project is this for?" — two different questions, and only the
                  second is the user's business at upload time. E3 2026-10-04:
                  "the entire dashboard itself is development and acquisitions…
                  so we do not need to specify." The track now comes from the
                  column default (migration 0180) and is set properly in the
                  Projects module, where the question belongs. */}
              <label htmlFor="kd-import-new-name">Name</label>
              <input
                id="kd-import-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="As your agency refers to it"
                autoComplete="off"
              />
              <div className="kd-import-new-actions">
                <button type="submit" disabled={newName.trim() === '' || saving}>
                  {saving ? 'Creating…' : 'Create and continue'}
                </button>
                {rows.length > 0 && (
                  <button type="button" onClick={() => setCreating(false)} disabled={saving}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          ) : (
            <div className="kd-import-projects" role="group" aria-label="Choose a project">
              {rows.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="kd-import-proj"
                  onClick={() => setChosen(p.id)}
                >
                  <span className="nm">{p.name}</span>
                  <span className="tr">{TRACK_LABEL[p.track]}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── The zone is NOT gated on having a project (2026-10-04) ──────────
          It was: `{active !== null && …}` hid the drop zone, the drag feedback,
          the preview and the queue until a project was chosen — so the screen a
          customer lands on with no projects yet had nothing to drop onto, which
          is what E3 saw and reported.

          That inverted this tab's own stated purpose: an agency with no projects
          "can create one from this tab rather than being sent to /projects/new
          and losing the file they were about to drop". The file has to be
          droppable for that sentence to mean anything.

          `editingTemplate` still suppresses it entirely — a template touches
          nothing, and that guard is the blank-slate rule, not a convenience. */}
      {!editingTemplate && (
        <div className="kd-import-zone">
          {/* Only when the CUSTOMER chose it here — a project handed down as
              context is already named on the surface around this tab, and
              repeating it would be noise. */}
          {projectId === null && (
            <div className="kd-import-zone-h">
              Importing into <b>{rows.find((r) => r.id === chosen)?.name ?? 'the selected project'}</b>
              {' · '}
              <button
                type="button"
                className="kd-linklike"
                onClick={() => setChosen(null)}
              >
                change
              </button>
            </div>
          )}
          {/* `active` is null until a project is named. The panel accepts the
              files regardless and holds them — signal 3, nobody starts over. */}
          <ImportPanel projectId={active} />
        </div>
      )}
    </div>
  );
}
